<template>
  <NolaNovelListItem v-if="novelFromNolaNovel" :novel="novel">
    <template #container>
      <div class="title">{{ title }}</div>
      <div class="data">
        <div>{{ $t('nolaNovelAssociatedNovelListItem.lastUpdate') }}{{ updatedAt }}</div>
        <div>{{ $t('nolaNovelAssociatedNovelListItem.publishStartDate') }}{{ publishedAt }}</div>
        <div>{{ $t('nolaNovelAssociatedNovelListItem.postedEpisodeCount') }}{{ episodeCount }}</div>
        <div>{{ $t('nolaNovelAssociatedNovelListItem.postedCharacterCount') }}{{ characterCount }}</div>
      </div>
      <div class="stats">
        <div class="stats-item">
          <star-outline-icon class="icon" :size="20" /><span>{{ review }}</span>
        </div>
        <div class="stats-item">
          <eye-outline-icon class="icon" :size="20" /><span>{{ pv }}</span>
        </div>
        <div class="stats-item">
          <bookmark-outline-icon class="icon" :size="20" /><span>{{ bookmark }}</span>
        </div>
        <div class="stats-item">
          <comment-text-outline-icon class="icon" :size="20" /><span>{{ comment }}</span>
        </div>
      </div>
    </template>
    <template #right>
      <button class="button primary" @click="onClickCreateEpisode">
        <div>{{ $t('nolaNovelAssociatedNovelListItem.postEpisodeButton') }}</div>
        <OpenInNewIcon class="icon" :size="16" />
      </button>
      <button class="button secondary" @click="onClickUpdateNovel">
        <div>{{ $t('nolaNovelAssociatedNovelListItem.updateNovelInfoButton') }}</div>
        <OpenInNewIcon class="icon" :size="16" />
      </button>
      <button class="button tertiary" @click="onClickTransNovel">
        <div>{{ $t('nolaNovelAssociatedNovelListItem.navigateToPublishedPageButton') }}</div>
        <OpenInNewIcon class="icon" :size="16" />
      </button>
    </template>
  </NolaNovelListItem>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import { format, getTime } from "date-fns";
import StarOutlineIcon from "icons/StarOutline.vue";
import EyeOutlineIcon from "icons/EyeOutline.vue";
import BookmarkOutlineIcon from "icons/BookmarkOutline.vue";
import CommentTextOutlineIcon from "icons/CommentTextOutline.vue";
import OpenInNewIcon from "icons/OpenInNew.vue";
import NolaNovelListItem from "@/components/molecules/NolaNovelListItem.vue";
import { AnalyticsItem, Novel, NovelFromNolaNovel } from "@/lib/models";
import { NolaNovelUrlGenerator } from "@/lib/utils/generator/nolanovelUrl";
import { createUrlWithUtmParams } from "@/lib/utils/url";
import { NolaItemId, NolaPageName } from "@/lib/utils/analytics";

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    StarOutlineIcon,
    EyeOutlineIcon,
    BookmarkOutlineIcon,
    CommentTextOutlineIcon,
    OpenInNewIcon,
    NolaNovelListItem,
  },
  props: {
    novel: Object as PropType<Novel>,
    novelFromNolaNovel: Object as PropType<NovelFromNolaNovel>,
  },
  computed: {
    novelIdFromNolaNovel() {
      const { associatedData } = this.novel;
      if (associatedData) return associatedData.nolaNovel.id;
      return undefined;
    },
    title() {
      return this.novel.title;
    },
    updatedAt() {
      const { episodes, updatedAt } = this.novelFromNolaNovel;

      if (!episodes || episodes.length === 0) return format(updatedAt, "YYYY/MM/DD");

      const publishedAts = episodes
        .map((e) => e.publishedAt ?? 0)
        .filter((p: number) => p > 0 && p < getTime(new Date()));

      if (publishedAts.length === 0) {
        return format(updatedAt, "YYYY/MM/DD");
      }
      const publishedAt = Math.max.apply(null, publishedAts);
      return format(publishedAt, "YYYY/MM/DD");
    },
    publishedAt() {
      const { episodes } = this.novelFromNolaNovel;

      if (!episodes || episodes.length === 0) return "-";

      const publishedAts = episodes
        .map((e) => e.publishedAt ?? 0)
        .filter((p: number) => p > 0 && p < getTime(new Date()));

      if (publishedAts.length === 0) {
        return "-";
      }
      const publishedAt = Math.min.apply(null, publishedAts);
      return format(publishedAt, "YYYY/MM/DD");
    },
    episodeCount() {
      const { episodes } = this.novelFromNolaNovel;

      if (!episodes) return 0;

      return episodes.length;
    },
    characterCount() {
      const { episodes } = this.novelFromNolaNovel;

      if (!episodes) return 0;

      return episodes.reduce((acc, cur) => acc + cur.characterCount, 0);
    },
    review() {
      const review = this.$store.getters["nolaNovelModule/analyticsById"]("REVIEW#ALL", this.novelIdFromNolaNovel) as
        | AnalyticsItem
        | undefined;
      return review ? review.count : 0;
    },
    pv() {
      const review = this.$store.getters["nolaNovelModule/analyticsById"](
        "PV#EPISODE#ALL",
        this.novelIdFromNolaNovel
      ) as AnalyticsItem | undefined;
      return review ? review.count : 0;
    },
    bookmark() {
      const review = this.$store.getters["nolaNovelModule/analyticsById"]("BOOKMARK#ALL", this.novelIdFromNolaNovel) as
        | AnalyticsItem
        | undefined;
      return review ? review.count : 0;
    },
    comment() {
      const review = this.$store.getters["nolaNovelModule/analyticsById"]("COMMENT#ALL", this.novelIdFromNolaNovel) as
        | AnalyticsItem
        | undefined;
      return review ? review.count : 0;
    },
  },
  methods: {
    async onClickCreateEpisode() {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error(this.$t('nolaNovelAssociatedNovelListItem.errors.nolaNovelUrlNotFound'));
      }

      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);
      const postEpisodeUrl = await service.genPostEpisodeUrl(this.novelIdFromNolaNovel!);
      window.open(
        createUrlWithUtmParams(postEpisodeUrl, NolaPageName.NolaNovel, NolaItemId.PostEpisodeButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
    async onClickUpdateNovel() {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error(this.$t('nolaNovelAssociatedNovelListItem.errors.nolaNovelUrlNotFound'));
      }

      const { novelIdFromNolaNovel, novel } = this;

      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);
      const updateNovelUrl = await service.genUpdateNovelUrl(novelIdFromNolaNovel!, novel.novelId);
      window.open(
        createUrlWithUtmParams(updateNovelUrl, NolaPageName.NolaNovel, NolaItemId.UpdateNovelButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
    async onClickTransNovel() {
      const url = process.env.VUE_APP_NOLANOVEL_WEB;

      if (!url) {
        throw new Error(this.$t('nolaNovelAssociatedNovelListItem.errors.nolaNovelUrlNotFound'));
      }

      const service = new NolaNovelUrlGenerator(process.env.VUE_APP_NOLANOVEL_WEB!);
      const novelUrl = await service.genMyPageNovelUrl(this.novelIdFromNolaNovel!);
      window.open(
        createUrlWithUtmParams(novelUrl, NolaPageName.NolaNovel, NolaItemId.NavigateToPublishedPageButton),
        process.env.VUE_APP_NOLANOVEL_NAME
      );
    },
  },
});

interface Props {
  novel: Novel;
  novelFromNolaNovel: NovelFromNolaNovel;
}

interface Data {}

interface Computed {
  novelIdFromNolaNovel?: string;
  title: string;
  updatedAt: string;
  publishedAt: string;
  episodeCount: number;
  characterCount: number;
  review: number;
  pv: number;
  bookmark: number;
  comment: number;
}

interface Methods {
  onClickCreateEpisode(): void;
  onClickUpdateNovel(): void;
  onClickTransNovel(): void;
}
</script>

<style lang="scss" scoped>
.data {
  grid-template-columns: repeat(2, 1fr);
  gap: 5px 20px;
  margin: 20px 0;
}

.button {
  margin: 0;
}
</style>
